import React from 'react';
import {Segmented, Badge, Tooltip} from 'antd';
import {ArrowRightOutlined, ExclamationCircleOutlined} from '@ant-design/icons';
import {getTagForAIProcessResponse} from "@src/components/Common/Tags";

interface PipelineStageSelectorProps {
    current: 0 | 1 | 2;
    onChange: (value: number | string) => void;
    dataPipeline: any;
}

const formatLabel = (key: string) =>
    key
        .replace(/[_-]/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());

const renderValue = (value: any) => {
    if (value === null || value === undefined || value === "")
        return <span style={{ color: "rgba(255,255,255,0.45)", fontStyle: "italic" }}>—</span>;
    if (typeof value === "boolean")
        return <span style={{ color: value ? "#52c41a" : "#ff7875" }}>{value ? "Yes" : "No"}</span>;
    if (typeof value === "object")
        return (
            <pre style={{ margin: 0, fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word", color: "#e6f7ff" }}>
                {JSON.stringify(value, null, 2)}
            </pre>
        );
    return <span style={{ color: "#fff" }}>{String(value)}</span>;
};

const renderBody = (body: any) => {
    const isPlainObject =
        body !== null && typeof body === "object" && !Array.isArray(body);

    if (!isPlainObject) {
        const text = typeof body === "string" ? body : JSON.stringify(body, null, 2);
        return (
            <div style={{ fontSize: 13, lineHeight: 1.5, color: "#fff", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {text}
            </div>
        );
    }

    const entries = Object.entries(body);

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {entries.map(([key, value]) => (
                <div key={key} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "#ffd666" }}>
                        {formatLabel(key)}
                    </span>
                    <div style={{ fontSize: 13, lineHeight: 1.5 }}>{renderValue(value)}</div>
                </div>
            ))}
        </div>
    );
};

const renderStageState = (response: any) => {
    const state = response?.state;
    const tag = getTagForAIProcessResponse(state);

    if (state !== "FAILED" && state !== "SCHEDULED")
        return tag;

    const body = response?.body;
    const hasBody = body !== undefined && body !== null && body !== "";

    if (!hasBody)
        return tag;

    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            {tag}
            <Tooltip
                color="#1f1f1f"
                title={
                    <div style={{ maxHeight: 320, overflow: "auto", padding: "4px 2px" }}>
                        {renderBody(body)}
                    </div>
                }
                styles={{ root: { maxWidth: 480 } }}
            >
                <ExclamationCircleOutlined style={{ color: "#faad14", cursor: "pointer" }} />
            </Tooltip>
        </span>
    );
};

const PipelineStageSelector = ({ current, onChange, dataPipeline }: PipelineStageSelectorProps) => {
    return (
        <div style={{ width: '100%', margin: '16px 0' }}>
            <Segmented
                block
                options={[
                    {
                        label: (
                            <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Badge count={1} style={{ backgroundColor: '#1890ff' }} />
                                    Config
                                    <span style={{ marginLeft: 8 }}>{renderStageState(dataPipeline?.ai_processor_response_config)}</span>
                                </span>
                                <ArrowRightOutlined />
                            </span>
                        ),
                        value: 0
                    },
                    {
                        label: (
                            <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Badge count={2} style={{ backgroundColor: '#1890ff' }} />
                                    Verify
                                    <span style={{ marginLeft: 8 }}>{renderStageState(dataPipeline?.ai_processor_response_verify)}</span>
                                </span>
                                <ArrowRightOutlined />
                            </span>
                        ),
                        value: 1
                    },
                    {
                        label: (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Badge count={3} style={{ backgroundColor: '#1890ff' }} />
                                Execution
                                <span style={{ marginLeft: 8 }}>{renderStageState(dataPipeline?.ai_processor_response_execution)}</span>
                            </span>
                        ),
                        value: 2
                    }
                ]}
                value={current}
                onChange={(val) => onChange(val)}
            />
        </div>
    );
};

export default PipelineStageSelector;
